import { useEffect, useRef, useState, useMemo } from 'react';
import { Button, Input, Select } from 'antd';
import {
  UpOutlined,
  DownOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { FolderNavigatorProps, FilterValue } from './comparisonResultTypes';
import { getDiffTypeLabel } from './comparisonResultTypes';
import styles from './ComparisonResult.module.scss';

const getFolderDiffBreakdown = (
  differenceItems: { differenceType: string; mismatchCount: number }[],
  mismatchDetails: { differenceType: string }[],
): { modified: number; onlyInA: number; onlyInB: number } => {
  const counts = { modified: 0, onlyInA: 0, onlyInB: 0 };
  for (const item of differenceItems) {
    if (item.mismatchCount === 0) {
      if (item.differenceType === 'Removed') counts.onlyInA++;
      else if (item.differenceType === 'Added') counts.onlyInB++;
      else if (item.differenceType !== 'Matching') counts.modified++;
    }
  }
  for (const detail of mismatchDetails) {
    // 'Matching' rows are context only, not actual differences, so exclude them
    if (detail.differenceType === 'Matching') continue;
    if (detail.differenceType === 'Removed') counts.onlyInA++;
    else if (detail.differenceType === 'Added') counts.onlyInB++;
    else counts.modified++;
  }
  return counts;
};

const FolderNavigator = ({
  folderResults,
  selectedFolderIndex,
  onSelectFolder,
  filterValue,
  onFilterChange,
  envAName,
  envBName,
}: FolderNavigatorProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [folderSearch, setFolderSearch] = useState('');

  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.querySelector<HTMLElement>('[data-active-folder="true"]');
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedFolderIndex]);

  const filteredFolders = useMemo(() => {
    let entries = folderResults.map((folder, idx) => ({ folder, originalIndex: idx }));
    if (filterValue === 'Matched') {
      entries = entries.filter(({ folder }) => folder.status === 'Matching');
    }
    if (folderSearch) {
      const q = folderSearch.toLowerCase();
      entries = entries.filter(({ folder }) => folder.folderName.toLowerCase().includes(q));
    }
    return entries;
  }, [folderResults, folderSearch, filterValue]);

  const selectableFolderIndices = useMemo(() => {
    if (filterValue === 'Matched') {
      return folderResults
        .map((folder, idx) => ({ status: folder.status, idx }))
        .filter(({ status }) => status === 'Matching')
        .map(({ idx }) => idx);
    }
    return folderResults
      .map((folder, idx) => ({ status: folder.status, idx }))
      .filter(({ status }) => status !== 'Matching')
      .map(({ idx }) => idx);
  }, [folderResults, filterValue]);

  const currentPositionInSelectable = selectableFolderIndices.indexOf(selectedFolderIndex);
  const totalSelectableFolders = selectableFolderIndices.length;

  const handlePrevious = () => {
    if (!totalSelectableFolders) return;
    const currentPos = currentPositionInSelectable;
    const prevPos = currentPos <= 0 ? totalSelectableFolders - 1 : currentPos - 1;
    onSelectFolder(selectableFolderIndices[prevPos]);
  };

  const handleNext = () => {
    if (!totalSelectableFolders) return;
    const currentPos = currentPositionInSelectable;
    const nextPos = currentPos >= totalSelectableFolders - 1 ? 0 : currentPos + 1;
    onSelectFolder(selectableFolderIndices[nextPos]);
  };

  const formatFolderStatus = (status: string): string => {
    switch (status) {
      case 'OnlyInA': return `Only in ${envAName}`;
      case 'OnlyInB': return `Only in ${envBName}`;
      default: return status;
    }
  };

  return (
    <div className={styles.navigator}>
      <div className={styles.navigatorHeader}>
        <h3 className={styles.navigatorTitle}>Folder Navigator</h3>
        <span className={styles.navigatorCount}>
          {filterValue === 'Matched'
            ? `${totalSelectableFolders} matching folder${totalSelectableFolders !== 1 ? 's' : ''}`
            : `${totalSelectableFolders} folder${totalSelectableFolders !== 1 ? 's' : ''} with differences`
          }
        </span>
      </div>

      <div className={styles.navigatorControls}>
        <Button
          size="small"
          icon={<UpOutlined />}
          onClick={handlePrevious}
          disabled={!totalSelectableFolders}
          className={styles.navButton}
        >
          Prev
        </Button>
        <div className={styles.navCounter}>
          <strong>{totalSelectableFolders ? currentPositionInSelectable + 1 : 0}</strong>
          <span> / {totalSelectableFolders}</span>
        </div>
        <Button
          size="small"
          icon={<DownOutlined />}
          onClick={handleNext}
          disabled={!totalSelectableFolders}
          className={styles.navButton}
        >
          Next
        </Button>
      </div>

      <div className={styles.navigatorFilters}>
        <Select<FilterValue>
          size="small"
          value={filterValue}
          onChange={onFilterChange}
          className={styles.filterSelect}
          options={[
            { value: 'all', label: 'All differences' },
            { value: 'Modified', label: getDiffTypeLabel('Modified', envAName, envBName) },
            { value: 'Added', label: getDiffTypeLabel('Added', envAName, envBName) },
            { value: 'Removed', label: getDiffTypeLabel('Removed', envAName, envBName) },
            { value: 'Unmatched', label: getDiffTypeLabel('Unmatched', envAName, envBName) },
            { value: 'Matched', label: 'Matched' },
          ]}
        />
        <Input
          size="small"
          placeholder="Search folders..."
          value={folderSearch}
          onChange={(e) => setFolderSearch(e.target.value)}
          allowClear
          className={styles.searchInput}
        />
      </div>

      <div ref={listRef} className={styles.folderList}>
        {filteredFolders.map(({ folder, originalIndex }) => {
          const isMatching = folder.status === 'Matching';
          const isFailed = folder.status === 'Failed';
          const isSelected = originalIndex === selectedFolderIndex;
          const breakdown = getFolderDiffBreakdown(folder.differenceItems, folder.mismatchDetails);

          return (
            <button
              key={folder.folderName}
              type="button"
              data-active-folder={isSelected ? 'true' : 'false'}
              className={`${styles.folderItem} ${isSelected ? styles.folderItemActive : ''} ${isMatching ? styles.folderItemMatching : ''}`}
              onClick={() => onSelectFolder(originalIndex)}
            >
              <span className={styles.folderItemLeft}>
                {isMatching ? (
                  <CheckCircleOutlined className={styles.matchingIcon} />
                ) : isFailed ? (
                  <WarningOutlined className={styles.failedIcon} />
                ) : null}
                <span className={styles.folderName}>{folder.folderName}</span>
              </span>
              <span className={styles.folderItemRight}>
                {isMatching ? (
                  <span className={styles.folderStatusLabel}>
                    {folder.nodeSummary?.nodesInA === 0 && folder.nodeSummary?.nodesInB === 0 ? 'Empty' : 'Matched'}
                  </span>
                ) : (
                  <>
                    {breakdown.modified > 0 && (
                      <span className={`${styles.mismatchBadge} ${styles.badgeModified}`}>{breakdown.modified}</span>
                    )}
                    {breakdown.onlyInA > 0 && (
                      <span className={`${styles.mismatchBadge} ${styles.badgeRemoved}`}>{breakdown.onlyInA}</span>
                    )}
                    {breakdown.onlyInB > 0 && (
                      <span className={`${styles.mismatchBadge} ${styles.badgeAdded}`}>{breakdown.onlyInB}</span>
                    )}
                  </>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <div className={styles.legend}>
        <span><i className={`${styles.legendDot} ${styles.dotModified}`} /> Changed</span>
        <span><i className={`${styles.legendDot} ${styles.dotOnlyInA}`} /> Only in {envAName}</span>
        <span><i className={`${styles.legendDot} ${styles.dotOnlyInB}`} /> Only in {envBName}</span>
        <span><i className={`${styles.legendDot} ${styles.dotUnmatched}`} /> Unable to Pair</span>
        <span><CheckCircleOutlined className={styles.matchingIcon} style={{ fontSize: 8, marginRight: 5 }} /> Matched</span>
      </div>
    </div>
  );
};

export default FolderNavigator;
